import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import {
    Collapse,
    Divider,
    FormControl,
    FormControlLabel,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Checkbox,
    TextField,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { fetchSkillsWithSubSkills, updateFtmSkills } from "../service";
import { theme } from "../../theme";
import { truncateText } from "../../utils";
import BtnSpinner from "../../common/BtnSpinner";
import { Context } from "../Provider";
import toast from "react-hot-toast";
import CardTemplate from "../../common/CardTemplate";
import debounce from "lodash.debounce";
import useIsMobile from "../../common/hooks/useIsMobile";

const styles = {
    container: { overflow: "auto", height: "400px" },
    subSkillLabel: { marginLeft: "30px" },
    fontSize: "15px",
};

const SubSkillList = ({ selectedSubSkills, subSkills, onSubSkillToggle }) => (
    <List component="div" disablePadding>
        <FormControl component="fieldset">
            {subSkills.map((subSkill) => (
                <FormControlLabel
                    key={subSkill.sub_skill_id}
                    control={
                        <Checkbox
                            size="small"
                            color={theme.main}
                            onChange={() => onSubSkillToggle(subSkill)}
                            checked={selectedSubSkills.some(
                                (s) => s.sub_skill_id === subSkill.sub_skill_id
                            )}
                            sx={{ '& .MuiSvgIcon-root': { fontSize: 23 } }}
                        />
                    }
                    label={truncateText(subSkill.sub_skill_name, 25)}
                    style={styles.subSkillLabel}
                    className="text-sm"
                />
            ))}
        </FormControl>
    </List>
);

const SkillItem = ({ skill, selectedSubSkills, onSkillSelect, onSubSkillToggle, isOpen }) => {
    const [open, setOpen] = useState(isOpen);

    const handleClick = () => {
        setOpen((prev) => !prev);
        onSkillSelect(skill);
    };

    return (
        <>
            <ListItemButton onClick={handleClick}>
                <ListItemIcon>
                    <i
                        className={`material-icons opacity-10 text-${theme.main}`}
                        style={{ fontSize: "35px" }}
                    >
                        {skill.skill_web_app_icon}
                    </i>
                </ListItemIcon>
                <ListItemText primary={<span style={styles.skillTitle}>{skill.skill_name}</span>} />
                {open ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <SubSkillList
                    selectedSubSkills={selectedSubSkills}
                    subSkills={skill.sub_skills}
                    onSubSkillToggle={onSubSkillToggle}
                />
            </Collapse>
            <Divider />
        </>
    );
};

const SkillSet = () => {
    const { state, updateCurrentStep } = useContext(Context);
    const { ftm, step } = state;

    const [skills, setSkills] = useState([]);
    const [selectedSubSkills, setSelectedSubSkills] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const isMobile = useIsMobile();

    useEffect(() => {
        const getSkillsWithSubSkills = async () => {
            try {
                const response = await fetchSkillsWithSubSkills();
                const fetchedSkills = response?.list || [];
                setSkills(fetchedSkills);

                const initialSelected = ftm?.ftm_skills?.reduce((acc, ftmSkill) => {
                    const subSkillId = ftmSkill.sub_skill?.sub_skill_id;
                    fetchedSkills.forEach((skill) => {
                        const found = skill.sub_skills.find(
                            (sub) => sub.sub_skill_id === subSkillId
                        );
                        if (found) acc.push(found);
                    });
                    return acc;
                }, []);

                setSelectedSubSkills(initialSelected || []);
            } catch (error) {
                console.error(error);
            }
        };

        getSkillsWithSubSkills();
    }, [ftm]);

    const handleSkillSelect = useCallback((skill) => {
        console.log("Selected Skill:", skill);
    }, []);

    const handleSubSkillToggle = useCallback((subSkill) => {
        updateCurrentStep(5);
        setSelectedSubSkills((prev) =>
            prev.some((s) => s.sub_skill_id === subSkill.sub_skill_id)
                ? prev.filter((s) => s.sub_skill_id !== subSkill.sub_skill_id)
                : [...prev, subSkill]
        );
    }, [updateCurrentStep]);

    const handleSelectedFtmSkills = async () => {
        try {
            setLoading(true);
            const updatedFtm = {
                ftm_id: ftm.ftm_id,
                skills: selectedSubSkills.map((subSkill) => ({
                    skill_id: subSkill.sub_skill_id,
                    status: 1,
                })),
            };

            const response = await updateFtmSkills(updatedFtm);
            if (response.status === "Success") {
                updateCurrentStep(6);
                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error("Registration failed:", error);
            toast.error("Registration failed.");
        } finally {
            setLoading(false);
        }
    };

    const filteredSkills = useMemo(() => {
        if (!search.trim()) return skills;

        const searchText = search.toLowerCase();

        return skills
            .map((skill) => {
                const isSkillMatch = skill.skill_name.toLowerCase().includes(searchText);

                const matchedSubSkills = skill.sub_skills.filter((subSkill) =>
                    subSkill.sub_skill_name.toLowerCase().includes(searchText)
                );

                if (isSkillMatch) {
                    // If skill name matches, return skill with all sub-skills
                    return { ...skill };
                } else if (matchedSubSkills.length > 0) {
                    // If sub-skills match, return skill with filtered sub-skills
                    return { ...skill, sub_skills: matchedSubSkills };
                }

                // No match
                return null;
            })
            .filter(Boolean); // Remove null entries
    }, [skills, search]);

    const debouncedSearchChange = useMemo(() => debounce((value) => {
        setSearch(value);
    }, 300), []);

    useEffect(() => {
        return () => {
            debouncedSearchChange.cancel();
        };
    }, [debouncedSearchChange]);

    return (
        <CardTemplate
            stepId={6}
            currentStep={step?.stepId}
            body={
                <>
                    <div className={`${!isMobile && "d-flex justify-content-between"} align-items-center mt-2`}>
                        <h6 className="mb-0">Select the trades you would like to receive work for</h6>
                        <TextField
                            placeholder="Search"
                            onChange={(e) => debouncedSearchChange(e.target.value)}
                            size="small"
                            className={isMobile && "w-100"}
                        />
                    </div>
                    <div className="mt-4 mx-3" style={styles.container}>
                        {filteredSkills.length > 0 ? (
                            <List sx={{ width: "100%", bgcolor: "background.paper" }} component="nav">
                                {filteredSkills.map(
                                    (skill) =>
                                        skill.sub_skills.length > 0 && (
                                            <SkillItem
                                                key={skill.skill_id}
                                                skill={skill}
                                                onSkillSelect={handleSkillSelect}
                                                selectedSubSkills={selectedSubSkills}
                                                onSubSkillToggle={handleSubSkillToggle}
                                                isOpen={ftm?.ftm_skills.some(
                                                    (item) => item.sub_skill?.skill?.skill_id === skill.skill_id
                                                )}
                                            />
                                        )
                                )}
                            </List>
                        ) : (
                            <div className="text-center text-muted mt-4">
                                No results found.
                            </div>
                        )}
                    </div>
                    {step?.stepId === 5 && (
                        <div className="mt-5 d-flex align-items-center justify-content-end">
                            <button
                                className={`btn bg-gradient-${theme.main} mb-0 ms-2 btn-sm ${isMobile && 'w-100'}`}
                                disabled={loading}
                                onClick={handleSelectedFtmSkills}
                            >
                                {loading ? <BtnSpinner /> : "PROCEED"}
                            </button>
                        </div>
                    )}
                </>
            }
        />
    );
};

export default SkillSet;